import React from 'react';
import Button from '@hubins/components/Button';
import LoadingBar from '@hubins/components/LoadingIndicators';
import NameInitialsBadge from '@hubins/components/NameInitialsBadge/NameInitialsBadge';
import BackofficePortfolioLink from '@hubins/components/BackofficePortfolioLink/BackofficePortfolioLink';
import BackofficeProfileLink from '@hubins/components/BackofficeProfileLink';
import { getMoney, formatDate } from 'helpers';
import Icon from '@hubins/components/IconNew';
import __ from 'localisation';

export const roleOptions = [
  { title: 'All', id: '' },
  { title: 'Hubins backoffice', id: 'hubins_backoffice' },
  { title: 'Hubins compliance', id: 'hubins_compliance' },
  { title: 'Quantum leben backoffice', id: 'quantum_backoffice' },
  { title: 'Quantum leben compliance', id: 'quantum_compliance' },
];

export const actionOptions = [
  { title: 'All', id: '' },
  { title: __('policy_approval'), id: 'portfolio_approval' },
  { title: __('sof_approval'), id: 'sof_approval' },
  { title: __('deposit_approval'), id: 'deposit_approval' },
  { title: __('tof_approval'), id: 'tof_approval' },
];

export const typeOptions = [
  { title: 'All', id: '' },
  { title: 'Source of funds', id: 'sof' },
  { title: 'Custom', id: 'custom' },
];

export const statusOptions = [
  { title: 'All', id: '' },
  { title: 'Active', id: 'active' },
  { title: 'Pending', id: 'pending' },
  { title: 'Approved', id: 'approved' },
  { title: 'Rejected', id: 'denied' },
];

const loadingRow =
  {
    created_at: <LoadingBar medium />,
    actions: [{ type: <LoadingBar medium key="loadingbar" /> }],
    profile: { name: <LoadingBar medium /> },
    portfolio: { short_name: <LoadingBar medium /> },
    navigation: <LoadingBar medium />,
    roles: [],
  };

export const loadingState = [
  loadingRow,
  loadingRow,
  loadingRow,
  loadingRow,
  loadingRow,
  loadingRow,
  loadingRow,
  loadingRow,
  loadingRow,
  loadingRow,
];

export const getStatusColor = (status) => {
  if (!status) {
    return 'neutral-3';
  }

  if (status === 'active') {
    return 'info';
  }

  if (status === 'approved') {
    return 'success';
  }
  if (status === 'comment') {
    return 'warning';
  }
  if (status === 'denied') {
    return 'danger';
  }
  if (status === 'pending') {
    return 'neutral-4';
  }

  return 'i-purple-2';
};

const isSLA = (row) => {
  if (row?.original?.completed_at) {
    return false;
  }

  const verdicts = row?.original?.verdicts;
  if (verdicts && verdicts.length > 0) {
    const sortedVerdicts = verdicts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    const latestVerdict = sortedVerdicts[0];
    const dateOfCreation = new Date(latestVerdict.created_at);
    const isDateMoreThen48hoursAgo = dateOfCreation < new Date(Date.now() - 48 * 60 * 60 * 1000);
    if (isDateMoreThen48hoursAgo) {
      return true;
    }
    return false;
  }

  const createdAt = new Date(row?.original?.created_at);
  const isDateMoreThen48hoursAgo = createdAt < new Date(Date.now() - 48 * 60 * 60 * 1000);
  if (isDateMoreThen48hoursAgo) {
    return true;
  }

  return false;
};

export const tableHeaders = [
  {
    Header: '',
    accessor: 'navigation',
    maxWidth: 40,
    Cell: ({ row }) => (
      <Button className='transparent small' onlyIcon icon="arrow-right" href={`/dashboard/approvals/approval/${row.original.uuid}`} />
    )
  },
  {
    Header: __("creation_date"),
    accessor: "created_at",
    Cell: ({ value }) => typeof value === 'string' ? formatDate(value) : value
  },
  {
    Header: __('type'),
    accessor: 'actions',
    Cell: ({ row }) => row.original.actions.map((action, index) => (typeof action.type === 'string' ? `${__(action.type)}${index < row.original.actions.length - 1 ? ', ' : ''}` : ''))
  },
  {
    Header: __('client'),
    accessor: 'profile.name',
    Cell: ({ row }) => (
      <>
        {row?.original?.profile?.name && (
          <BackofficeProfileLink profileUuid={row?.original?.profile?.uuid}>
            {row?.original?.profile?.name}
          </BackofficeProfileLink>
        )}
      </>
    )
  },
  {
    Header: __('policy'),
    accessor: 'portfolio.short_name',
    Cell: ({ row }) => (
      <>
        {row?.original?.portfolio?.short_name && (
          <BackofficePortfolioLink portfolioUuid={row?.original?.portfolio?.uuid} profileUuid={row?.original?.profile?.uuid}>
            {row?.original?.portfolio?.short_name}
          </BackofficePortfolioLink>
        )}
      </>
    )
  },
  {
    Header: __('asset'),
    accessor: 'sections2',
    Cell: ({ row }) => {
      const value = row?.original?.sections;
      if (value && Array.isArray(value) && value.length > 0) {
        const stringValue = value.reduce((acc, section) => {
          if (section.type === 'tof' && section?.params?.security) {
            acc.push(section?.params?.security);
          }
          if (section.type === 'deposit') {
            acc.push(__('asset_cash'));
          }
          return acc;
        }, []);
        return stringValue.join(', ');
      }
      return '';
    }
  },
  {
    Header: __('value'),
    accessor: 'sections',
    Cell: ({ row }) => {
      const value = row?.original?.sections;
      if (value && Array.isArray(value) && value.length > 0) {
        const stringValue = value.reduce((acc, section) => {
          if (section.type === 'tof' && section?.params?.trade_amount) {
            acc.push(getMoney(section?.params?.trade_amount));
          }
          if (section.type === 'deposit') {
            acc.push(getMoney(section?.params?.trade_amount, section?.params?.currency || 'SEK'));
          }
          return acc;
        }, []);
        return stringValue.join(', ');
      }
      return '';
    }
  },
  // {
  //   Header: __('status'),
  //   accessor: 'status',
  //   Cell: ({ row }) => {
  //     try {

  //       const archived = row.original.status === 'archived';

  //       if (archived) {
  //         return __('archived');
  //       }

  //       const isCompleted = row.original.actions.every((action) => {
  //         return action.status === 'executed';
  //       });
  //       if (isCompleted) {
  //         return __('completed');
  //       } else {
  //         const isRejected = row.original.roles.some((role) => {
  //           return role.status === 'denied';
  //         });
  //         if (isRejected) {
  //           return __('denied');
  //         } else {
  //           const isInProgress = row.original.verdicts.length > 0;
  //           if (isInProgress) {
  //             return __('in_progress');
  //           } else {
  //             return __('pending');
  //           }
  //         }
  //       }
  //     } catch {
  //       return __('loading');
  //     }
  //   }
  // },
  {
    Header: __('assigned'),
    accessor: 'assigned',
    Cell: ({ row }) => {
      try {
        const roles = row.original.roles;
        const foundActiveRole = roles.find((role) => role.status === 'active');
        const assignedUser = (foundActiveRole && foundActiveRole.user_name) ? foundActiveRole.user_name : '';

        return (
          <span>
            {assignedUser}
          </span>
        );
      } catch {
        return '';
      }
    }
  },
  {
    Header: __('stage'),
    accessor: 'stage',
    Cell: ({ value }) => __(value)
  },
  {
    Header: __('verdicts'),
    accessor: 'verdicts',
    Cell: ({ row }) => (
      <div>
        {(row.original.verdicts || []).map((verdict, index) => (
          <NameInitialsBadge className='inline-flex' size="small" name={verdict.user_name} color={getStatusColor(verdict.status)} key={`ver-${index}`} />
        ))}
      </div>
    )
  },
  // {
  //   Header: __('priority'),
  //   accessor: 'priority',
  //   Cell: ({ row }) => (
  //     __(row.original.priority || 'standard')
  //   )
  // },


  // },
  // {
  //   Header: __('due_date'),
  //   accessor: 'deadline',
  // },
  // {
  //   Header: "Processing time",
  //   accessor: "processing_time",
  //   Cell: ({ row }) => (
  //     <span>
  //       TODO
  //     </span>
  //   )
  // },
  // {
  //   Header: 'Priority',
  //   accessor: 'priority',
  //   Cell: ({ row }) => (
  //     <span>
  //       TODO
  //     </span>
  //   )
  // },
  {
    Header: 'SLA',
    accessor: 'sla',
    width: 50,
    Cell: ({ row }) => {
      const sla = isSLA(row);
      return (
        <div className={`${sla ? 'sla' : ''}`}>
          {sla && <Icon icon="Sign Warning" size={24} />}
        </div>
      );
    }
  }
];



const roleKeys = {
  'hubins_backoffice': 'hubinsBackoffice',
  'hubins_compliance': 'hubinsCompliance',
  'quantum_backoffice': 'quantumBackoffice',
  'quantum_compliance': 'quantumCompliance',
};

export const hasApproved = (userRoles, approval) => {
  return userRoles.some((role) => {
    if (typeof roleKeys[role] === 'undefined') return false;
    return approval[roleKeys[role]] === 'approved';
  });
};

export const keyToIcon = {
  'a_policy_1': 'Common File Text by Streamlinehq',
  'a_policy_2': 'Graph Stats Ascend by Streamlinehq',
  'a_policy_3': 'Products Briefcase by Streamlinehq',
  'a_policy_4': 'Upload Bottom by Streamlinehq',
  'a_policy_5': 'Withdrawal',
  'a_policy_6': 'Common File Stack by Streamlinehq',
  'a_policy_7': 'Saving Money Flower by Streamlinehq',
  'a_policy_verdict': 'check signle',
};
